import { v4 as uuidv4 } from 'uuid';
import { cloneDeep, isEmpty } from 'lodash';
import { ContractHTMLBlock } from '~/models/blocks/contract-html-block.model';
import { ISmartField } from '~/types/model.types';
import { SmartBlockType, SmartFieldType } from '~/types/builder.types';
import { calculateSmartFieldOptionName } from '~/utils/block.utils';
import {
    OLD_SMART_FIELD_TYPE_TO_NEW_TYPE,
    PRESET_CATEGORIES
} from './old-smart-field-type-to-new-type.constant';
import { scrollToElement } from '~/components/Builder/Block/rootBlockAutoScroll';
import { isInViewport } from '~/utils/dom.utils';
import { toFixedIfNotRound } from '~/utils/money.utils';
import { getSelectedText } from '~/utils/ckeditor.utils';
import type { BlockController } from '~/controllers/block.controller';
import type { FlowController } from '~/controllers/flow.controller';
import type { TCKEditor } from '~/components/common/CKEditorWrapper/ckeditor.types';

export const createSmartField = ({
    contractHTMLBlock,
    blockCtrl,
    flowCtrl,
    editor,
    type,
    name
}: {
    contractHTMLBlock: ContractHTMLBlock;
    blockCtrl: BlockController;
    flowCtrl: FlowController;
    editor;
    type;
    name;
}) => {
    const newField: Omit<ISmartField, 'field_id'> = {
        name: '',
        type: name,
        default_value: '',
        placeholder: '',
        fill_type: 'client_optional',
        data_source_property: ''
    };

    if (type === SmartFieldType.Inline) {
        if (name === 'initials') {
            newField.name = 'initials';
            newField.data_source_property = 'initials';
            newField.data_source_type = 'first_client';
            newField.placeholder = 'First client initials';
        }
    } else if (name === SmartBlockType.Payments) {
        newField.data_source_type = 'payments';
        newField.data_source_property = 'recap';
        newField.default_value = null;
    }

    return createSmartFieldWithData({
        contractHTMLBlock,
        blockCtrl,
        flowCtrl,
        field: newField,
        editor,
        smartFieldType: type
    });
};

export function createSmartFieldWithData({
    contractHTMLBlock,
    blockCtrl,
    flowCtrl,
    field,
    editor,
    smartFieldType
}: {
    contractHTMLBlock: ContractHTMLBlock;
    blockCtrl: BlockController;
    flowCtrl: FlowController;
    field: Omit<ISmartField, 'field_id'>;
    editor: TCKEditor;
    smartFieldType: SmartFieldType;
}) {
    const fieldId = uuidv4();
    const newField: ISmartField = { ...field, field_id: fieldId };

    if (smartFieldType === SmartFieldType.Inline) {
        if (newField.data_source_property === 'initials') {
            newField.fill_type = 'client_required';
        }
    }

    if (!newField.data_source_type) {
        const selectedText = getSelectedText({ editor });
        if (selectedText) {
            newField.name = 'custom';
            newField.data_source_type = 'custom';
            newField.default_value = selectedText;
        }
    }

    contractHTMLBlock.contract.fields_metadata.push(newField);

    editor.execute('hbSmartField', {
        id: fieldId,
        type: smartFieldType,
        data: { type: newField.type }
    });

    blockCtrl.updateBlock(
        contractHTMLBlock,
        {
            contract: {
                fields_metadata: contractHTMLBlock.contract.fields_metadata,
                html_body: editor.getData()
            }
        },
        {
            oldData: { contract: cloneDeep(contractHTMLBlock.contract) },
            applySideEffectsCallback: () =>
                setTimeout(() =>
                    flowCtrl.selectContractSmartField(fieldId, newField.type)
                )
        }
    );

    const element = document.querySelector(`[data-field-id='${fieldId}']`);
    if (!isInViewport(element)) {
        scrollToElement({
            top: element?.getBoundingClientRect().top - 200,
            behavior: 'smooth'
        });
    }

    return newField;
}

export const deleteContractRecaps = (block: ContractHTMLBlock, blockCtrl) => {
    const recapsIds = block.recaps.map(recap => recap.field_id);
    if (isEmpty(recapsIds)) {
        return;
    }

    const newContractHtml = getContractHtmlAfterSmartBlocksRemoved(
        block,
        blockCtrl,
        recapsIds
    ).new_html_body;

    blockCtrl.updateBlock(block, {
        contract: {
            fields_metadata: block.fields,
            html_body: newContractHtml
        }
    });
};

export const getContractHtmlAfterSmartBlocksRemoved = (
    block: ContractHTMLBlock,
    blockCtrl,
    smartBlocksIds: string[]
) => {
    const el = document.createElement('div');
    el.innerHTML = block.contract.html_body;
    smartBlocksIds.forEach(smartBlockId => {
        const smartBlockContainer = el.querySelector(
            `div[data-field-id="${smartBlockId}"]`
        );

        if (smartBlockContainer) {
            smartBlockContainer.parentNode.removeChild(smartBlockContainer);
        }
    });

    return {
        new_html_body: el.innerHTML
    };
};

export const GenerateSmartFieldsID = (contractHtmlBlock: ContractHTMLBlock) => {
    const newMetaData = [];

    const contract = contractHtmlBlock.contract;
    // Create an element with the contract body
    const el = document.createElement('div');
    el.innerHTML = contract.html_body;

    const smartFieldsElements = el.getElementsByClassName('hb-smart-field');
    const smartBlocksElements = el.getElementsByClassName('hb-smart-box');
    Array.from([...smartFieldsElements, ...smartBlocksElements]).forEach(
        smartField => {
            const newID = uuidv4();
            const currID = smartField.attributes['data-field-id'].value;
            smartField.setAttribute('data-field-id', newID);

            const field = contract.fields_metadata.find(
                metadata => metadata.field_id === currID
            );

            if (field) {
                const newField: ISmartField = {
                    field_id: newID,
                    name: field.name,
                    type: field.type,
                    default_value: field.default_value,
                    placeholder: field.placeholder,
                    fill_type: field.fill_type,
                    data_source_property: field.data_source_property,
                    data_source_sub_property: field.data_source_sub_property,
                    data_source_type: field.data_source_type,
                    source_field_id: field.field_id,
                    source_variable_id: field.connected_variable_id
                };

                newMetaData.push(newField);
            }
        }
    );

    return {
        html_body: el.innerHTML,
        fields_metadata: newMetaData
    };
};

// TODO: add control z local storage
// TODO: all paste validation

let oldContract = null;

export const UpdateMetadataOnPaste = (data, block) => {
    const allNewFields = [];
    const { optionalFields } = block as ContractHTMLBlock;
    oldContract = cloneDeep((block as ContractHTMLBlock).contract);
    data.forEach(fieldData => {
        const newField: ISmartField = {
            field_id: fieldData.newID,
            name: '',
            type: 'field',
            default_value: '',
            placeholder: '',
            fill_type: 'client_optional',
            data_source_property: ''
        };

        const localstorageMetadata: null | ISmartField[] = JSON.parse(
            localStorage.getItem('copiedFieldsMetadata')
        );

        const fieldMetadata = localstorageMetadata?.find(
            localField => localField.field_id === fieldData.pastedFieldID
        );
        let fieldValue = null;
        if (optionalFields) {
            if (fieldMetadata?.data_source_type === 'custom') {
                fieldValue = fieldMetadata.default_value;
            } else {
                fieldValue = optionalFields.find(
                    option =>
                        calculateSmartFieldOptionName(option) ===
                        fieldMetadata?.name
                )?.value;
            }
        }

        if (fieldData) {
            newField.name = fieldMetadata?.name;
            newField.type = fieldMetadata?.type;
            newField.default_value = fieldValue || '';
            newField.placeholder = fieldMetadata?.placeholder;
            newField.fill_type = fieldMetadata?.fill_type;
            newField.data_source_property = fieldMetadata?.data_source_property;
            newField.data_source_sub_property =
                fieldMetadata?.data_source_sub_property;
            newField.data_source_type = fieldMetadata?.data_source_type;
        }

        allNewFields.push(newField);
    });

    (block as ContractHTMLBlock).contract.fields_metadata.push(...allNewFields);
};

export const UpdateBlockAfterPaste = (block, editor, blockCtrl) => {
    blockCtrl.updateBlock(
        block,
        {
            contract: {
                fields_metadata: (block as ContractHTMLBlock).contract
                    .fields_metadata,
                html_body: editor.getData()
            }
        },
        {
            oldData: oldContract
                ? {
                      contract: oldContract
                  }
                : undefined
        }
    );
};

export const SmartFieldCopy = (smartFieldIds, block) => {
    const copiedFieldsMetadata = [];
    smartFieldIds.forEach(id => {
        const field = (block as ContractHTMLBlock).fieldsMap[id];
        if (field) {
            copiedFieldsMetadata.push(field);
        }
    });

    // we have to store it in local storage so it will work between tabs
    localStorage.setItem(
        'copiedFieldsMetadata',
        JSON.stringify(copiedFieldsMetadata)
    );
};

let copiedSmartFieldsIds: string[];

const getIdFromChild = child => {
    const isChildSmartField =
        typeof child.getAttribute === 'function' &&
        child.getAttribute('data-field-id');

    if (isChildSmartField) {
        copiedSmartFieldsIds.push(child.getAttribute('data-field-id'));
    }
    if (child.childCount > 0) {
        Array.from(child.getChildren()).forEach(newChild => {
            getIdFromChild(newChild);
        });
    }
};

export const GetSmartFieldsIdFromCopyText = children => {
    copiedSmartFieldsIds = [];

    Array.from(children).forEach(child => {
        getIdFromChild(child);
    });

    return copiedSmartFieldsIds;
};

// old field parsing data-cke-saved-name data
const parseFieldConfig = fieldConfig => {
    const res = (fieldConfig || '').match(/(\w+):[^,}]+/g);
    const fieldData = {};
    if (res) {
        res.forEach(entry => {
            const split = entry.split(':');
            fieldData[split[0]] = (split[1] || '').trim();
        });
    }

    return fieldData;
};

export const migratePasteOfOldField = (data, block) => {
    const contractHTMLBlock = block as ContractHTMLBlock;

    data.forEach(field => {
        const jsonData: any = parseFieldConfig(field.oldFiledData);

        let field_name = '';
        let data_source_property = '';
        let data_source_sub_property;
        let data_source_type = '';
        let default_value = field.fieldValue;

        if (jsonData.field_name === 'other') {
            field_name = `CUSTOM:${jsonData.field_placeholder}`;
        } else {
            const option =
                OLD_SMART_FIELD_TYPE_TO_NEW_TYPE[jsonData.field_name];

            if (option) {
                data_source_property = option.data_source_property;
                data_source_sub_property = option.data_source_sub_property;
                data_source_type = option.data_source_type;
                const smartFieldFromServer =
                    contractHTMLBlock.findMatchingOptionalField({
                        data_source_property,
                        data_source_sub_property,
                        data_source_type
                    });

                option.category =
                    option.category || PRESET_CATEGORIES[data_source_type];
                field_name = calculateSmartFieldOptionName(option);
                default_value =
                    jsonData.field_changed === 'true' && field.fieldValue
                        ? field.fieldValue
                        : smartFieldFromServer?.value || '';
            } else {
                // That's a field that wasn't presented in the new contract yet so it is migrated as a user custom field
                field_name = `CUSTOM:${jsonData.field_placeholder}`;
            }
        }

        const data_fill_type =
            // eslint-disable-next-line no-nested-ternary
            jsonData.field_hb_data_type === 'client_field'
                ? jsonData.field_required
                    ? 'client_required'
                    : 'client_optional'
                : 'vendor_required';

        const newField: ISmartField = {
            field_id: field.newID,
            name: field_name,
            type: field.type || 'field',
            default_value,
            placeholder: jsonData.field_placeholder,
            fill_type: data_fill_type,
            data_source_type: data_source_type || '',
            data_source_property: data_source_property || field_name,
            data_source_sub_property
        };

        (block as ContractHTMLBlock).contract.fields_metadata.push(newField);
    });
};

export const PastedRecap = (data, block) => {
    data.forEach(field => {
        const isPaymentRecap = field.type === 'payments_recap';
        const newField: ISmartField = {
            field_id: field.newID,
            name: field.type,
            type: field.type,
            placeholder: '',
            fill_type: 'client_optional',
            default_value: null,
            data_source_type: isPaymentRecap ? 'payments' : '',
            data_source_property: isPaymentRecap ? 'recap' : ''
        };

        (block as ContractHTMLBlock).contract.fields_metadata.push(newField);
    });
};

export const UpdateMetadataOnBoxPaste = (data, block) => {
    const allNewFields = [];
    oldContract = cloneDeep((block as ContractHTMLBlock).contract);
    data.forEach(fieldData => {
        const fieldType = JSON.parse(fieldData.pastedFieldType);
        const isPaymentRecap = fieldType === 'payments_recap';
        const newField: ISmartField = {
            field_id: fieldData.newID,
            name: '',
            type: fieldType?.type,
            default_value: null,
            placeholder: '',
            fill_type: 'client_optional',
            data_source_type: isPaymentRecap ? 'payments' : '',
            data_source_property: isPaymentRecap ? 'recap' : ''
        };

        allNewFields.push(newField);
    });

    (block as ContractHTMLBlock).contract.fields_metadata.push(...allNewFields);
};

export const previewVSmartFieldsValues = {
    total_price: invoice => toFixedIfNotRound(invoice.grandTotal),
    sub_total: invoice => toFixedIfNotRound(invoice.subTotal),
    total_tax: invoice => toFixedIfNotRound(invoice.totalTax),
    late_fee_percentage: invoice => toFixedIfNotRound(invoice.lateFeePercentage)
};
